import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { notificationsAPI, approvalsAPI, authAPI } from '../utils/api';
import LangToggle from './common/LangToggle';
import { useTranslation } from 'react-i18next';

const NAV = [
  { to: '/dashboard',      icon: '📊', label: 'nav.overview' },
  { to: '/faculty',        icon: '🎓', label: 'nav.faculty' },
  { to: '/batches',        icon: '🗓️', label: 'nav.schedule' },
  { to: '/students',       icon: '👥', label: 'nav.students' },
  { to: '/payments',       icon: '💳', label: 'nav.payments' },
  { to: '/financial-hub',  icon: '💰', label: 'nav.financialHub' },
  { to: '/attendance',     icon: '👥', label: 'nav.attendance' },
  { to: '/tutor-attendance', icon: '🎓', label: 'nav.tutorAttendance' },
  { to: '/closures',       icon: '🏖️', label: 'nav.closures' },
  { to: '/enquiries',      icon: '💬', label: 'nav.enquiries' },
  { to: '/approvals',      icon: '📝', label: 'nav.approvals' },
  { to: '/library',        icon: '📚', label: 'nav.library' },
  { to: '/merchandise',    icon: '🛍️', label: 'nav.merchandise' },
  { to: '/password-reset', icon: '🔑', label: 'nav.passwordReset' },
  { to: '/universal-search', icon: '🔍', label: 'nav.universalSearch' },
  { to: '/landing-manager',icon: '🌐', label: 'nav.website' },
  { to: '/settings',       icon: '⚙️', label: 'nav.settings' },
];

export default function Layout({ children, title }) {
  const { t, i18n } = useTranslation();
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const notifRef = useRef(null);

  // Profile Edit State
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileData, setProfileData] = useState({ name: '', email: '', mobile: '' });
  const [profileSaving, setProfileSaving] = useState(false);

  const handleLogout = () => { logout(); navigate('/'); };

  const [approvalsCount, setApprovalsCount] = useState(0);
  const isFetchingStatsRef = useRef(false);

  const fetchStats = async () => {
    if (isFetchingStatsRef.current) return;
    if (typeof document !== 'undefined' && document.hidden) return;

    isFetchingStatsRef.current = true;
    try {
      const [notifRes, appRes] = await Promise.all([
        notificationsAPI.getMy({ limit: 20 }),
        approvalsAPI.getPendingApprovalsCount()
      ]);
      if (notifRes?.data) {
        setNotifications(notifRes.data.notifications || []);
        setUnreadCount(notifRes.data.unreadCount || 0);
      }
      if (appRes?.data?.success) {
        setApprovalsCount(appRes.data.count || 0);
      }
    } catch (e) {
      // Quietly ignore network blips or timeouts
    } finally {
      isFetchingStatsRef.current = false;
    }
  };

  useEffect(() => {
    if (admin) {
      fetchStats();
      const interval = setInterval(fetchStats, 60000);
      return () => clearInterval(interval);
    }
  }, [admin?._id || admin?.email]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMarkAsRead = async (id, isRead) => {
    if (isRead) return;
    try {
      await notificationsAPI.markAsRead(id);
      fetchStats();
    } catch (e) {}
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationsAPI.markAllAsRead();
      fetchStats();
      setShowNotifications(false);
    } catch (e) {}
  };

  return (
    <div className="dash-layout">
      {/* Overlay */}
      <div
        className={`dash-sidebar-overlay ${sidebarOpen ? 'show' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Sidebar */}
      <aside className={`dash-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="dash-sidebar-logo">
          <img src="/assets/logo.png" alt="ETS Logo" />
          <div className="logo">
            ETS
            <span>Every Talent Shines</span>
          </div>
        </div>

        <nav className="dash-sidebar-nav">
          {NAV.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/dashboard'}
              className={({ isActive }) => `dash-nav-item${isActive ? ' active' : ''}`}
              onClick={() => setSidebarOpen(false)}
            >
              <span className="dash-nav-icon">{item.icon}</span>
              {t(item.label)}
              {item.to === '/approvals' && approvalsCount > 0 && (
                <span style={{ marginLeft: 'auto', background: 'var(--wine, #8B0000)', color: 'white', fontSize: '0.7rem', padding: '2px 8px', borderRadius: '12px', fontWeight: 'bold' }}>
                  {approvalsCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="dash-sidebar-footer">
          {admin && (
            <div className="dash-admin-info">
              <div className="dash-admin-name" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {admin.name}
                <button 
                  onClick={() => {
                    setProfileData({ name: admin.name || '', email: admin.email || '', mobile: admin.mobile || '' });
                    setShowProfileModal(true);
                  }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--wine)' }}
                  title="Edit Profile"
                >
                  ✏️
                </button>
              </div>
              <div className="dash-admin-contact">{admin.mobile || admin.email}</div>
            </div>
          )}
          <button className="dash-logout-btn" onClick={handleLogout}>
            <span>🚪</span> {t('common.signOut')}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="dash-main">
        {/* Topbar */}
        <header className="dash-topbar">
          <div className="dash-topbar-left">
            <button className="dash-topbar-ham" onClick={() => setSidebarOpen(p => !p)}>☰</button>
            <div className="dash-topbar-title">{title || 'ETS'}</div>
          </div>
          <div className="dash-topbar-right" style={{ gap: '16px' }}>
            <LangToggle />
            <div className="dash-topbar-bell-wrapper" ref={notifRef}>
              <button 
                className="dash-topbar-bell" 
                title="Notifications"
                onClick={() => setShowNotifications(p => !p)}
              >
                🔔
                {unreadCount > 0 && <span className="dash-topbar-bell-badge">{unreadCount}</span>}
              </button>

              {showNotifications && (
                <div className="dash-notification-dropdown">
                  <div className="dash-notification-header">
                    <h3>{t('common.notifications')}</h3>
                    {unreadCount > 0 && (
                      <button 
                        style={{fontSize: 11, background: 'none', border: 'none', color: 'var(--wine)', cursor: 'pointer', fontWeight: 600}} 
                        onClick={handleMarkAllAsRead}
                      >
                        {t('common.markAllRead')}
                      </button>
                    )}
                  </div>
                  <div className="dash-notification-list">
                    {notifications.length === 0 ? (
                      <div style={{padding: 20, textAlign: 'center', color: '#888', fontSize: 12}}>{t('common.noNewNotifs')}</div>
                    ) : (
                      notifications.map(n => (
                        <div key={n._id || n.id} className={`dash-notification-item ${!n.isRead ? 'unread' : ''}`} onClick={() => handleMarkAsRead(n._id || n.id, n.isRead)}>
                          <div className="dash-notification-item-icon">
                            {n.category === 'financial' ? '💳' : n.category === 'studio' ? '🎬' : '📢'}
                          </div>
                          <div className="dash-notification-item-content">
                            <div className="dash-notification-item-title">{i18n.language === 'zh' && n.titleZh ? n.titleZh : n.title}</div>
                            <div className="dash-notification-item-message">{i18n.language === 'zh' && n.messageZh ? n.messageZh : n.message}</div>
                            <div className="dash-notification-item-time">{new Date(n.createdAt).toLocaleString()}</div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            <button className="btn btn-sm btn-outline dash-topbar-logout hide-mobile" onClick={handleLogout}>{t('common.signOut')}</button>
          </div>
        </header>

        {/* Page Content */}
        <main className="dash-content">{children}</main>
      </div>

      {/* Edit Profile Modal */}
      {showProfileModal && (
        <div className="modal-overlay" onClick={() => setShowProfileModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <div className="modal-header">
              <h3>{t('common.editProfile')}</h3>
              <button className="close-btn" onClick={() => setShowProfileModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group" style={{ marginBottom: 15 }}>
                <label className="form-label-style">{t('common.fullName')}</label>
                <input 
                  type="text" 
                  className="form-input-style"
                  value={profileData.name} 
                  onChange={e => setProfileData({...profileData, name: e.target.value})} 
                />
              </div>
              <div className="form-group" style={{ marginBottom: 15 }}>
                <label className="form-label-style">{t('common.email')}</label>
                <input 
                  type="email" 
                  className="form-input-style"
                  value={profileData.email} 
                  onChange={e => setProfileData({...profileData, email: e.target.value})} 
                />
              </div>
              <div className="form-group" style={{ marginBottom: 25 }}>
                <label className="form-label-style">{t('common.mobile')}</label>
                <input 
                  type="text" 
                  className="form-input-style"
                  value={profileData.mobile} 
                  onChange={e => setProfileData({...profileData, mobile: e.target.value})} 
                />
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button className="btn btn-outline" onClick={() => setShowProfileModal(false)}>{t('common.cancel')}</button>
                <button 
                  className="btn btn-primary" 
                  disabled={profileSaving}
                  onClick={async () => {
                    setProfileSaving(true);
                    try {
                      await authAPI.updateProfile(profileData);
                      alert('Profile updated successfully! Please login again to sync data.');
                      setShowProfileModal(false);
                      handleLogout();
                    } catch (e) {
                      alert(e.response?.data?.message || 'Failed to update profile');
                    } finally {
                      setProfileSaving(false);
                    }
                  }}
                >
                  {profileSaving ? t('common.saving') : t('common.save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
